import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { InputAdornment } from '@mui/material';

import FormInput from '@shared/atoms/FormFields/FormInput';
import Drawer from '@shared/atoms/Drawer';
import LanguageSelect from '@shared/molecules/LanguageSelect';
import theme from '@shared/themes/default';

import { DOMAIN_LOCALES } from '@shared/const';
import { useForm } from '@shared/utils';

import { useAppGlobals } from '../../../../../contexts/AppContext';

const DrawerContent = styled.div`
  background: ${theme.palette.darkDistinct};
  margin: ${theme.spacing.s} ${theme.spacing.s} 0;
  padding: ${theme.spacing.s};
  width: 496px;
`;

const FormRow = styled.div`
  display: flex;
  column-gap: ${theme.spacing.s};
  align-items: flex-end;
`;

const LanguageWrapper = styled.div`
  flex: 0 0 auto;
`;

const InputWrapper = styled.div`
  flex: 1;
`;

const AttachmentDrawer = ({ attachment, onClose, onSave, open }) => {
  const { t } = useTranslation();
  const { domain } = useAppGlobals();

  const languages = DOMAIN_LOCALES[domain];

  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);

  const {
    disableSubmit,
    edited,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    setDefaults,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      name: {
        localised: true,
        required: true,
      },
      ext: {
        required: true,
      },
    },
    onSubmit: onSave,
    locales: languages,
  });

  const splitFullname = (fullname) => {
    if (fullname) {
      const newFilenameArray = fullname.split('.');
      const ext = newFilenameArray.length > 1 ? newFilenameArray.pop() : null;
      const newFilenameName = newFilenameArray.join('.');
      return { name: newFilenameName, ext };
    }
    return {};
  };

  useEffect(() => {
    if (attachment) {
      let ext = '';
      const defaultTranslation = attachment.name.find(
        (translation) => translation.languageCode === languages[0]
      );
      const splitName = splitFullname(defaultTranslation?.value);

      if (splitName?.ext) {
        ext = splitName.ext;
      }

      const processedNames = attachment.name.map((translation) => {
        const split = splitFullname(translation.value);
        return {
          ...translation,
          value: split.name || translation.value,
        };
      });

      setDefaults({
        name: processedNames,
        ext,
      });
    }
  }, [attachment, languages]);

  useEffect(() => {
    if (languages.length > 0 && !languages.includes(selectedLanguage)) {
      setSelectedLanguage(languages[0]);
    }
  }, [languages, selectedLanguage]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      onSave={handleSubmit}
      title={t('backoffice.courses.attachmentsTab.drawerTitle')}
      edited={edited}
      saveDisabled={disableSubmit}
    >
      <DrawerContent>
        <FormRow>
          {languages.length > 1 && (
            <LanguageWrapper>
              <LanguageSelect
                languages={languages}
                onChange={({ value }) => setSelectedLanguage(value)}
                required
                value={selectedLanguage}
              />
            </LanguageWrapper>
          )}
          <InputWrapper>
            <FormInput
              id="name"
              name="name"
              theme="dark"
              label={t('backoffice.courses.attachmentsTab.name')}
              locale={selectedLanguage}
              placeholder={t(
                'backoffice.courses.attachmentsTab.filenamePlaceholder'
              )}
              errors={errors}
              handleBlur={handleBlur}
              handleChange={handleChange}
              properties={properties}
              values={values}
              endAdornment={
                values?.ext ? (
                  <InputAdornment position="start">{`.${values?.ext}`}</InputAdornment>
                ) : null
              }
            />
          </InputWrapper>
        </FormRow>
      </DrawerContent>
    </Drawer>
  );
};

AttachmentDrawer.propTypes = {
  attachment: PropTypes.shape({
    name: PropTypes.arrayOf(
      PropTypes.shape({
        languageId: PropTypes.string,
        languageCode: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool,
};

export default AttachmentDrawer;
